import React from 'react';

import { Box, Typography, IconButton, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { DeleteOutline, Menu } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';

import { DCM, DOCS, IMAGE, THREE, VIDEO } from '../../models/Resource';
import useBlocRef from '../../utils/useBlocRef.hook';
import AmbraContainer from '../ambraContainer/AmbraContainer';
import ImageWrapper from '../common/ImageWrapper';
import VideoWrapper from '../common/VideoWrapper';
import ObjViewer from '../objViewer/ObjViewer';
import PdfViewer from '../pdfViewer/PdfViewer';

const useStyles = makeStyles(() => ({
  deleteButton: {
    '&:hover': {
      color: '#F44336',
    },
    color: '#9E9E9E',
  },
  dragHandle: {
    color: '#757575',
    cursor: 'grab',
    marginRight: 10,
  },
  imageContainer: {
    alignItems: 'center',
    background: '#172B4D',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    borderRadius: '8px',
    color: '#2AAAE2',
    display: 'flex',
    height: '52px',
    justifyContent: 'center',
    marginRight: 10,
    width: '64px',
  },
  imageTypography: {
    color: '#2AAAE2',
    fontSize: '14px',
    fontWeight: 'bold',
    lineHeight: 1,
    marginBottom: 0,
  },
  materialInfo: {
    alignItems: 'center',
    display: 'flex',
    flexGrow: 1,
  },
  materialItem: {
    '&:hover p': {
      color: '#2AAAE2',
    },
    alignItems: 'center',
    backgroundColor: '#F5F5F5',
    borderRadius: 5,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    padding: 10,
  },
  materialTitle: {
    color: '#37474F',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
  },
  nonSortable: {
    '&:hover p': {
      color: '#2AAAE2',
    },
    cursor: 'pointer',
  },
}));

const DragHandle = SortableHandle(({ classes }) => (
  <div className={classes.dragHandle}>
    <Menu fontSize="small" />
  </div>
));

const SortableItem = SortableElement(({ material, classes, onRemove, onSelect }) => {
  const materialResource = material?.resource || material?.material?.resource;
  const materialType = materialResource?.type;
  let imageText = '';

  if (materialType === THREE) {
    imageText = '3D';
  } else if (materialType === DOCS) {
    imageText = 'DOC';
  } else if (materialType === DCM) {
    imageText = 'DCM';
  }

  return (
    <Box className={classes.materialItem}>
      <DragHandle classes={classes} />

      <div style={{ backgroundImage: `url(${materialResource?.thumbnailPath})` }} className={classes.imageContainer} onClick={() => onSelect(material)} aria-hidden="true">
        {imageText}
      </div>

      <Box className={classes.materialInfo} onClick={() => onSelect(material)}>
        <Typography className={classes.materialTitle} title="Open Material">
          {material.title || material.material?.title || 'Untitled Material'}
        </Typography>
      </Box>

      <Tooltip title="Remove Material">
        <IconButton size="small" onClick={() => onRemove(material.material?.id || material.materialId || material.id)} className={classes.deleteButton}>
          <DeleteOutline fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
});

const SortableList = SortableContainer(({ materials, classes, onRemove, onSelect }) => (
  <Box display="flex" flexDirection="column" gridGap={15}>
    {materials.map((material, index) => (
      <SortableItem key={`material-${material.id}`} index={index} material={material} classes={classes} onRemove={onRemove} onSelect={onSelect} />
    ))}
  </Box>
));

const MaterialItem = ({ material, classes, onSelect }) => {
  const materialResource = material?.resource || material?.material?.resource;
  const materialType = materialResource?.type;
  let imageText = '';

  if (materialType === THREE) {
    imageText = '3D';
  } else if (materialType === DOCS) {
    imageText = 'DOC';
  } else if (materialType === DCM) {
    imageText = 'DCM';
  }

  return (
    <Box className={`${classes.materialItem} ${classes.nonSortable}`} onClick={() => onSelect(material)}>
      <div style={{ backgroundImage: `url(${materialResource?.thumbnailPath})` }} className={classes.imageContainer}>
        {imageText}
      </div>

      <Box className={classes.materialInfo}>
        <Typography className={classes.materialTitle}>{material.title || material.material?.title || 'Untitled Material'}</Typography>
      </Box>
    </Box>
  );
};

const MaterialsList = ({ materials, onRemove, sortable, onSortEnd }) => {
  const classes = useStyles();
  const resourcesBlocRef = useBlocRef('resourcesBloc');
  const { enqueueSnackbar } = useSnackbar();

  const handleMaterialClick = (material) => {
    const materialResource = material?.resource || material?.material?.resource;
    const materialData = material?.material || material;

    if (!materialResource) {
      enqueueSnackbar('Cannot open material: Invalid material data', { variant: 'error' });
      return;
    }

    try {
      const materialForViewer = {
        ...materialData,
        h264Path: materialResource.h264Path,
        isReady: true,
        path: materialResource.path,
        type: materialResource.type,
      };

      resourcesBlocRef.current.openViewer(materialForViewer, { paper: classes.imagePaper }, { onClose: () => {} });
    } catch (err) {
      console.error('Error opening material viewer:', err);
      enqueueSnackbar('Failed to open material viewer', { variant: 'error' });
    }
  };

  if (sortable && onSortEnd) {
    return <SortableList materials={materials} classes={classes} onRemove={onRemove} onSortEnd={onSortEnd} useDragHandle lockAxis="y" onSelect={handleMaterialClick} />;
  }

  return (
    <Box display="flex" flexDirection="column" gridGap={15}>
      {materials.map((material) => (
        <MaterialItem key={`material-${material.id}`} material={material} classes={classes} onSelect={handleMaterialClick} />
      ))}
    </Box>
  );
};

export default MaterialsList;
